import { useRef, useState, useEffect } from 'react'
import { useAuth, getAndClearIntendedUrl } from '../../auth'
import { AUTH_EXPIRED_MESSAGE_KEY } from '../../auth/services/apiClient'
import { useNavigate, useLocation, Link } from 'react-router-dom'

import { Messages } from 'primereact/messages'
import fullLogoLight from '../../client/logo/logo-full-light.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

const Login: React.FC = () => {
    const { login, setAppInitiated } = useAuth()

    const navigate = useNavigate()
    const location = useLocation()

    const usernameRef = useRef<HTMLInputElement>(null)
    const errRef = useRef<HTMLDivElement>(null)

    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [isLoggingIn, setIsLoggingIn] = useState(false)
    const [errMsg, setErrMsg] = useState('')
    const [infoMsg, setInfoMsg] = useState('')
    const msgs = useRef<any>(null)

    useEffect(() => {
        const expiredMsg = sessionStorage.getItem(AUTH_EXPIRED_MESSAGE_KEY)
        if (expiredMsg) {
            setInfoMsg(expiredMsg)
            sessionStorage.removeItem(AUTH_EXPIRED_MESSAGE_KEY)
        }
    }, [])

    useEffect(() => {
        setErrMsg('')
    }, [username, password])

    useEffect(() => {
        if (errMsg) {
            msgs.current?.show({
                id: '1',
                sticky: true,
                severity: 'error',
                summary: '',
                detail: errMsg,
                closable: false,
            })
        } else {
            msgs.current?.clear()
        }
    }, [errMsg])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        setErrMsg('')
        setIsLoggingIn(true)

        try {
            const response = await login({ username, password })

            if (response.success) {
                setUsername('')
                setPassword('')

                const intendedUrl = getAndClearIntendedUrl()
                const from = location.state?.from?.pathname || intendedUrl
                navigate(from, { replace: true })
            }
        } catch (err: unknown) {
            const axiosErr = err as {
                response?: {
                    status?: number
                    data?: { initiated?: boolean; errors?: string[] }
                }
            }

            if (typeof axiosErr?.response?.data?.initiated !== 'undefined') {
                setAppInitiated(axiosErr.response.data.initiated!)
            }

            if (!axiosErr?.response) {
                setErrMsg('No Server Response')
            } else if (axiosErr.response?.status === 400) {
                setErrMsg('Missing Username or Password')
            } else if (
                axiosErr.response?.status === 401 ||
                axiosErr.response?.status === 403
            ) {
                setErrMsg(
                    axiosErr.response.data?.errors?.join('<br />') ||
                        'Login Failed'
                )
            } else {
                setErrMsg('Login Failed')
            }
        } finally {
            setIsLoggingIn(false)
        }
    }

    return (
        <section className="bg-gradient-primary vh-100">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-xl-10 col-lg-12 col-md-9">
                        <div className="card o-hidden border-0 shadow-lg my-5">
                            <div className="card-body p-0">
                                <div className="row">
                                    <div className="col-lg-6 d-none d-lg-block bg-login-image" />
                                    <div className="col-lg-6">
                                        <div className="p-5">
                                            <div className="text-center">
                                                <div className="p-4 text-center w-100 border-bottom border-primary mb-4">
                                                    <img
                                                        src={fullLogoLight}
                                                        alt="Full Logo"
                                                        className="full-logo"
                                                    />
                                                </div>
                                                <h1 className="h4 mb-4">
                                                    Welcome Back!
                                                </h1>
                                                {infoMsg && (
                                                    <div
                                                        className="alert alert-warning small text-start mb-3"
                                                        role="alert"
                                                    >
                                                        {infoMsg}
                                                    </div>
                                                )}
                                                <div
                                                    ref={errRef}
                                                    className={
                                                        'text-danger small ' +
                                                        (errMsg
                                                            ? 'errmsg'
                                                            : 'offscreen')
                                                    }
                                                    aria-live="assertive"
                                                >
                                                    <Messages ref={msgs} />
                                                </div>
                                            </div>
                                            <form
                                                className="user"
                                                onSubmit={handleSubmit}
                                            >
                                                <div className="form-group mb-3">
                                                    <input
                                                        type="text"
                                                        ref={usernameRef}
                                                        className="form-control form-control-user p-3"
                                                        id="username"
                                                        aria-describedby="usernameHelp"
                                                        placeholder="Enter Username..."
                                                        onChange={(e) =>
                                                            setUsername(
                                                                e.target.value
                                                            )
                                                        }
                                                        value={username}
                                                        required
                                                    />
                                                </div>
                                                <div className="form-group mb-3">
                                                    <input
                                                        type="password"
                                                        className="form-control form-control-user p-3"
                                                        id="password"
                                                        placeholder="Password"
                                                        onChange={(e) =>
                                                            setPassword(
                                                                e.target.value
                                                            )
                                                        }
                                                        value={password}
                                                        required
                                                    />
                                                </div>
                                                <div className="form-group mb-3">
                                                    <div className="custom-control custom-checkbox small">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id="customCheck"
                                                        />
                                                        <label
                                                            className="custom-control-label ms-2"
                                                            htmlFor="customCheck"
                                                        >
                                                            Remember Me
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className="text-center d-grid gap-2">
                                                    <button
                                                        className="btn btn-primary btn-user d-block"
                                                        disabled={isLoggingIn}
                                                    >
                                                        {isLoggingIn ? (
                                                            <span>
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faCircleNotch
                                                                    }
                                                                    spin={true}
                                                                />{' '}
                                                                Logging in...
                                                            </span>
                                                        ) : (
                                                            'Login'
                                                        )}
                                                    </button>
                                                </div>
                                            </form>
                                            <div className="text-center mt-3">
                                                <Link
                                                    to={'/forgot-password'}
                                                    className="small"
                                                >
                                                    Forgot Password?
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Login
